import React, { useEffect, useState } from 'react'
import { onIdTokenChanged } from 'firebase/auth'
import { MailCheck, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { auth } from '../config/firebase'
import { useUser } from '../contexts/UserContext'
import { isAdminUser } from '../utils/userProfileFields'
import { toastCustomerError } from '../utils/customerError'
import {
  VERIFY_EMAIL_MESSAGE,
  needsEmailVerification,
  resendVerificationEmail,
  refreshEmailVerification
} from '../services/accountService'

const RESEND_COOLDOWN_MS = 60 * 1000

export default function VerifyEmailBanner() {
  const { user } = useUser()
  const [needsVerify, setNeedsVerify] = useState(() => needsEmailVerification(auth.currentUser))
  const [busy, setBusy] = useState(null)
  const [resentAt, setResentAt] = useState(0)

  useEffect(() => onIdTokenChanged(auth, (fbUser) => setNeedsVerify(needsEmailVerification(fbUser))), [])
  useEffect(() => {
    if (!resentAt) return undefined
    const timer = setTimeout(() => setResentAt(0), RESEND_COOLDOWN_MS)
    return () => clearTimeout(timer)
  }, [resentAt])

  if (!needsVerify || !user || isAdminUser(user)) return null

  const coolingDown = Date.now() - resentAt < RESEND_COOLDOWN_MS

  const handleResend = async () => {
    setBusy('resend')
    try {
      await resendVerificationEmail()
      setResentAt(Date.now())
      toast.success(`We sent a new link to ${auth.currentUser?.email || 'your email'}.`)
    } catch (err) {
      const message = err?.code === 'auth/too-many-requests'
        ? 'We just sent a link. Please check your inbox and try again in a few minutes.'
        : "We couldn't send the email. Please try again."
      toastCustomerError(message, message)
    } finally {
      setBusy(null)
    }
  }

  const handleRefresh = async () => {
    setBusy('refresh')
    try {
      const verified = await refreshEmailVerification()
      if (verified) {
        setNeedsVerify(false)
        toast.success('Thanks! Your email is verified.')
      } else {
        toast.error("We don't see a verified email yet. Please open the link in your inbox, then try again.")
      }
    } catch (err) {
      toastCustomerError(err, "We couldn't check your email. Please try again.")
    } finally {
      setBusy(null)
    }
  }

  return (
    <div
      role="status"
      className="mb-4 flex flex-col gap-3 rounded-xl border border-gold/40 bg-gold/10 px-4 py-3 text-ivory sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex min-w-0 items-start gap-3">
        <MailCheck className="mt-0.5 h-5 w-5 shrink-0 text-gold" aria-hidden="true" />
        <div className="min-w-0">
          <p className="font-medium">{VERIFY_EMAIL_MESSAGE}</p>
          <p className="text-sm text-ivory-muted break-words">
            Open the link we sent to {auth.currentUser?.email || 'your email'}, then come back here.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 sm:shrink-0 sm:flex-nowrap">
        <button
          type="button"
          onClick={handleResend}
          disabled={busy !== null || coolingDown}
          className="btn-secondary flex-1 whitespace-nowrap px-3 sm:flex-none sm:px-4"
        >
          {busy === 'resend' && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {coolingDown ? 'Email sent' : 'Resend verification email'}
        </button>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={busy !== null}
          className="btn-primary flex-1 whitespace-nowrap px-3 sm:flex-none sm:px-4"
        >
          {busy === 'refresh' && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          I've verified
        </button>
      </div>
    </div>
  )
}
